import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PremiumUpgradeModal from './PremiumUpgradeModal';
import LoginPromptModal from './LoginPromptModal';

interface Props {
  children: React.ReactNode;
  featureName?: string;
}

/**
 * Guard component for premium-only routes.
 * Redirects non-premium users to /premium page after showing upgrade modal.
 */
export default function PremiumGuard({ children, featureName = 'fitur ini' }: Props) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [checked, setChecked] = useState(false);

  const isPremium = user?.subscription?.status === 'active' && 
    user?.subscription?.endDate && 
    new Date(user.subscription.endDate) > new Date();

  useEffect(() => {
    if (loading) return;

    // Not logged in
    if (!user) {
      setShowLoginModal(true);
      setChecked(true);
      return;
    }

    // Logged in but not premium
    if (!isPremium) {
      setShowUpgradeModal(true);
      setChecked(true);
      return;
    }

    // Premium user - allow access
    setChecked(true);
  }, [user, loading, isPremium]);

  const handleLoginClose = () => {
    setShowLoginModal(false);
    navigate('/');
  };

  const handleUpgradeClose = () => {
    setShowUpgradeModal(false);
    navigate('/premium');
  };

  // Still loading
  if (loading || !checked) {
    return null;
  }

  // Show login modal for non-logged in users
  if (!user) {
    return (
      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={handleLoginClose}
        message={`Login untuk mengakses ${featureName}`}
      />
    );
  }

  // Show upgrade modal for non-premium users
  if (!isPremium) {
    return (
      <PremiumUpgradeModal
        isOpen={showUpgradeModal}
        onClose={handleUpgradeClose}
        featureName={featureName}
      />
    );
  }

  // Premium user - render children
  return <>{children}</>;
}
